import React from "react";
import { useCurrentFrame, useVideoConfig, spring, interpolate } from "remotion";

export interface SubtitleCue {
  from: number;
  to: number;
  text: string;
  highlightWords?: string[]; // Words that receive special accent styling
}

interface KineticSubtitleProps {
  cues: SubtitleCue[];
  accentColor?: string; // e.g. "#10B981"
  textColor?: string;
  fontSize?: number;
  bottomPosition?: number; // Default 580px (Within TikTok/Reels Safe Zone)
}

export const KineticSubtitle: React.FC<KineticSubtitleProps> = ({
  cues,
  accentColor = "#10B981",
  textColor = "#FFFFFF",
  fontSize = 38,
  bottomPosition = 580,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const currentCue = cues.find((c) => frame >= c.from && frame < c.to);
  if (!currentCue) return null;

  // Split sentence into individual words
  const words = currentCue.text.split(" ");
  const duration = currentCue.to - currentCue.from;
  const elapsed = frame - currentCue.from;

  // Calculate which word is currently being spoken based on elapsed time ratio
  const activeWordIndex = Math.min(
    words.length - 1,
    Math.floor((elapsed / duration) * words.length)
  );

  return (
    <div
      style={{
        position: "absolute",
        left: 80,
        right: 80,
        bottom: bottomPosition,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px 14px",
        pointerEvents: "none",
        zIndex: 99,
      }}
    >
      {words.map((word, idx) => {
        const isActive = idx === activeWordIndex;
        const isHighlight =
          currentCue.highlightWords &&
          currentCue.highlightWords.some((h) =>
            word.toLowerCase().replace(/[.,!]/g, "").includes(h.toLowerCase().replace(/[.,!]/g, ""))
          );

        // Word pop spring animation when it activates
        const wordSpring = spring({
          frame: elapsed - (idx / words.length) * duration,
          fps,
          config: {
            stiffness: 220,
            damping: 14,
            mass: 0.6,
          },
        });

        const scale = isActive
          ? interpolate(wordSpring, [0, 1], [1.22, 1.0])
          : 1.0;

        // High-contrast accessibility:
        // Active word: Pure White pop or Neon Gold
        // Future/Past words: Pure White at 0.7 opacity, crisp and readable against dark background
        const isCurrentOrPast = idx <= activeWordIndex;
        const color = isActive
          ? (isHighlight ? "#FDE047" : "#FFFFFF")
          : isHighlight
          ? "#34D399"
          : "#E5E7EB";

        const opacity = isActive ? 1.0 : isCurrentOrPast ? 0.85 : 0.6;

        const textShadow = isActive
          ? `0 0 28px ${isHighlight ? "rgba(253, 224, 71, 0.9)" : "rgba(16, 185, 129, 0.9)"}, 0 2px 10px rgba(0,0,0,0.9)`
          : "0 2px 8px rgba(0,0,0,0.8)";

        return (
          <span
            key={idx}
            style={{
              fontSize,
              fontWeight: isActive || isHighlight ? 800 : 700,
              color,
              opacity,
              transform: `scale(${scale})`,
              textShadow,
              transition: "color 0.15s ease, opacity 0.15s ease",
              display: "inline-block",
              margin: "0 6px",
              letterSpacing: "-0.01em",
            }}
          >
            {word}
          </span>
        );
      })}
    </div>
  );
};
